// AvatarCustomizer — a Wavelength-style character builder for a member's
// avatar. A big live preview on the left; four tabbed slot pickers on the
// right (Character / Hat / Outfit / Backdrop). Choosing an option updates the
// preview instantly; "Save" persists via PATCH /members/me/avatar.
import { useCallback, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { CharacterAvatar } from "./CharacterAvatar";
import {
  type AvatarBackdrop,
  BACKDROPS,
  type AvatarCharacter,
  CHARACTERS,
  DEFAULT_AVATAR,
  type AvatarConfig,
  type AvatarHat,
  HATS,
  type AvatarOutfit,
  type Option,
  OUTFITS,
} from "./catalog";
import { updateAvatar } from "./avatar-api";
import "./avatar.css";

type SlotKey = "character" | "hat" | "outfit" | "backdrop";

const SLOTS: { key: SlotKey; label: string; options: Option<string>[] }[] = [
  { key: "character", label: "Character", options: CHARACTERS },
  { key: "hat", label: "Hat", options: HATS },
  { key: "outfit", label: "Outfit", options: OUTFITS },
  { key: "backdrop", label: "Backdrop", options: BACKDROPS.map((b) => ({ id: b.id, label: b.label, emoji: "" })) },
];

export interface AvatarCustomizerProps {
  /** The avatar to start editing from (the member's current config). */
  initial?: AvatarConfig | null;
  /** Called with the new config after a successful save. */
  onSaved: (config: AvatarConfig) => void;
  /** Close without saving. */
  onClose: () => void;
}

export function AvatarCustomizer({
  initial,
  onSaved,
  onClose,
}: AvatarCustomizerProps): ReactElement {
  const [draft, setDraft] = useState<AvatarConfig>({ ...DEFAULT_AVATAR, ...initial });
  const [activeSlot, setActiveSlot] = useState<SlotKey>("character");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeOptions = useMemo(
    () => SLOTS.find((s) => s.key === activeSlot)?.options ?? [],
    [activeSlot],
  );

  const pick = useCallback((slot: SlotKey, id: string) => {
    setDraft((d) => ({ ...d, [slot]: id }));
  }, []);

  const randomize = useCallback(() => {
    // Vary by array position — no Math.random needed for a fun shuffle: rotate
    // each slot to a different option based on the current selection index.
    setDraft((d) => ({
      character: CHARACTERS[(CHARACTERS.findIndex((o) => o.id === d.character) + 3) % CHARACTERS.length].id as AvatarCharacter,
      hat: HATS[(HATS.findIndex((o) => o.id === d.hat) + 2) % HATS.length].id as AvatarHat,
      outfit: OUTFITS[(OUTFITS.findIndex((o) => o.id === d.outfit) + 2) % OUTFITS.length].id as AvatarOutfit,
      backdrop: BACKDROPS[(BACKDROPS.findIndex((o) => o.id === d.backdrop) + 1) % BACKDROPS.length].id as AvatarBackdrop,
    }));
  }, []);

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateAvatar(draft);
      onSaved(updated.avatar);
    } catch {
      setError("Could not save your avatar. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [draft, onSaved, saving]);

  const selectedId = draft[activeSlot];

  return (
    <div className="avatar-customizer" data-testid="avatar-customizer">
      <div className="avatar-customizer__body">
        {/* Live preview */}
        <div className="avatar-customizer__preview">
          <CharacterAvatar avatar={draft} size={160} />
          <button
            type="button"
            className="avatar-customizer__random"
            onClick={randomize}
            disabled={saving}
          >
            🎲 Surprise me
          </button>
        </div>

        {/* Slot pickers */}
        <div className="avatar-customizer__panel">
          <div className="avatar-customizer__tabs" role="tablist" aria-label="Avatar parts">
            {SLOTS.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={activeSlot === s.key}
                className={
                  "avatar-customizer__tab" +
                  (activeSlot === s.key ? " avatar-customizer__tab--active" : "")
                }
                onClick={() => setActiveSlot(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div
            className="avatar-customizer__options"
            role="listbox"
            aria-label={`${activeSlot} options`}
          >
            {activeOptions.map((opt) => {
              const previewCfg: AvatarConfig = { ...draft, [activeSlot]: opt.id };
              const selected = selectedId === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  title={opt.label}
                  className={
                    "avatar-customizer__option" +
                    (selected ? " avatar-customizer__option--selected" : "")
                  }
                  onClick={() => pick(activeSlot, opt.id)}
                >
                  <CharacterAvatar avatar={previewCfg} size={52} />
                  <span className="avatar-customizer__option-label">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {error && (
        <p className="avatar-customizer__error" role="alert">
          {error}
        </p>
      )}

      <div className="avatar-customizer__actions">
        <button
          type="button"
          className="avatar-customizer__cancel"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="avatar-customizer__save"
          onClick={save}
          disabled={saving}
          data-testid="avatar-save"
        >
          {saving ? "Saving…" : "Save avatar"}
        </button>
      </div>
    </div>
  );
}

export default AvatarCustomizer;
